import React, { useCallback, useEffect } from 'react';
import { Form, Formik } from 'formik';

import Typography from 'components-new/typography/typography';
import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';

import { createLocale } from 'locales';

import SubmitButton from 'form-elements/submit-button';
import TextField from 'form-elements/text-field';
import PasswordField from 'form-elements/password-field';

import useFreelancerLogin from 'state/remote/remote-freelancer/use-freelancer-login';

import useRecaptcha from 'hooks/use-recaptcha';

import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';
import freelancerRoutes from '../freelancer-routes';

import loginFormValidate from './schema';

const loginLocalize = createLocale('freelancerLoginLocales');
const globalLocalize = createLocale('global');

const Login = (): JSX.Element => {
  const { login, initialValues, isLoading, error } = useFreelancerLogin();
  const {
    validateRecaptcha,
    reCaptchaLoaded,
    error: recaptchaError,
  } = useRecaptcha();
  const onLoginClicked = useCallback(
    async (values: Parameters<typeof login>[0]) => {
      const validateResponse = await validateRecaptcha('freelancerLogin');
      if (validateResponse.success) {
        const success = await login(values);
        if (success) {
          window.location.pathname = freelancerRoutes.welcome.path;
          showSuccessToast({
            description: loginLocalize('loginSuccess'),
            title: globalLocalize('notification'),
          });
        }
      } else {
        showErrorToast({
          description: loginLocalize('loginError'),
          title: globalLocalize('notification'),
        });
      }
    },
    [login, validateRecaptcha],
  );

  useEffect(() => {
    if (error)
      showErrorToast({
        description: error.message,
        title: globalLocalize('error'),
      });
    if (recaptchaError)
      showErrorToast({
        description: recaptchaError,
        title: globalLocalize('error'),
      });
  }, [error, recaptchaError]);

  return (
    <FreelancerSignUpPageContainer
      showHeader
      headerTitle={loginLocalize('title')}
      showGoBackButton
      className="flex flex-col items-center"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={loginFormValidate}
        onSubmit={onLoginClicked}
      >
        <Form className="mb-16 w-full" autoComplete="false">
          <TextField
            name="username"
            autoComplete="false"
            aria-autocomplete="none"
            type="text"
            className="mt-4"
            title={globalLocalize('userName')}
          />
          <PasswordField
            name="password"
            type="password"
            className="mt-4"
            title={globalLocalize('password')}
          />
          <Typography
            as="h1"
            variant="body-medium"
            className="w-full mt-3 text-gray500"
          >
            {loginLocalize('passwordForgotten')}
            {'? '}
            <a
              href="/password_reset/"
              className=" text-current text-purple font-bold"
            >
              {globalLocalize('clickHere')}
            </a>
          </Typography>
          <SubmitButton
            type="submit"
            label={globalLocalize('login')}
            variant="primary"
            className="w-full justify-center mt-10"
            loading={isLoading || (!reCaptchaLoaded && !recaptchaError)}
            disabled={!reCaptchaLoaded}
          />
        </Form>
      </Formik>
    </FreelancerSignUpPageContainer>
  );
};

export default Login;
